<template>
  <div class="leaders-table-container">
    <!-- Header with Add Button -->
    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>{{$t('general.leader_board_rules')}}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button class="btn btn-default dropdown-arrow" type="button" @click="openAddModal">
          {{$t('general.add')}}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" >
      <default-loading></default-loading>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchLeaderBoardData" class="retry-button">
        {{ $t('general.try_again') }}
      </button>
    </div>

    <!-- Data Table -->
    <div v-else class="table-wrapper">
      <table class="leaders-table">
        <thead>
          <tr>
            <th class="kriter-column">{{$t('general.criterion')}}</th>
            <th class="toplam-column">{{$t('general.total')}}</th>
            <th class="veri-column">{{$t('general.data')}}</th>
            <th class="action-column">{{$t('general.transactions')}}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in leaderBoardData" :key="item.id">
            <td>{{ getTypeDisplayName(item.type) }}</td>
            <td>{{ formatTotal(item.total) }}</td>
            <td>{{ formatDataWithNames(item) }}</td>
            <td class="action-cell">
              <div class="action-buttons">
                <button class="btn btn-sm btn-primary" @click="editRule(item)">
                  <i class="uil-edit"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="deleteRule(item)">
                  <i class="uil-trash-alt"></i>
                </button>
              </div>
            </td>
          </tr>
          
          <!-- Empty State -->
          <tr v-if="leaderBoardData.length === 0">
            <td colspan="5" class="empty-state">
               {{$t('general.no_records_found')}}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Leader Rule Modal Component (Add & Edit) -->
    <LeaderRuleModal 
      ref="leaderRuleModal" 
      :editing-rule="editingRule"
      :existing-rules="leaderBoardData"
      @rule-added="onRuleAdded" 
      @rule-updated="onRuleUpdated"
      @modal-closed="onModalClosed"
    />
  </div>
</template>

<script>
import { GET_ITEM_DETAIL_BY_ID } from "@/core/services/store/REST.module";
import { showError, showSuccess, showConfirm } from "@/utils/alertUtils.js";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import LeaderRuleModal from "./components/AddLeaderRuleModal.vue";
import leaderBoardMixin from "@/view/company/LeadersTableManagement/mixin/leaderBoardMixin.js";
import ApiService from "@/core/services/api.service.js";
import { confirmDialogDelete } from "@/utils/confirmDialog";

export default {
  name: "LeadersTableManagement",
  components: { 
    DefaultLoading,
    LeaderRuleModal
  },
  mixins: [leaderBoardMixin],
  data() {
    return {
      leaderBoardData: [],
      isLoading: false,
      error: null,
      editingRule: null // Düzenlenen kural
    };
  },
  mounted() {
    this.fetchLeaderBoardData();
  },
  methods: {
    async fetchLeaderBoardData() {
      this.isLoading = true;
      this.error = null;

      try {
        const result = await this.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
          url: 'api/leader-board-rules/',
          acceptPromise: true,
        });

        if (result.status) {
          this.leaderBoardData = result.data?.data || [];

          // Fetch all necessary data to resolve IDs to names
          await this.fetchAllReferenceData();
        } else {
          this.error = 'Veri yüklenirken bir hata oluştu';
          showError(this.error);
        }
      } catch (error) {
        this.error = 'API bağlantısında hata oluştu';
        showError(this.error);
        console.error('Leader board data fetch error:', error);
      } finally {
        this.isLoading = false;
      }
    },

    formatTotal(total) {
      if (total === 0) return '0';
      if (total === null || total === undefined) return this.$t('general.no_value');
      return String(total);
    },

    openAddModal() {
      this.editingRule = null; // Ekleme modu
      this.$refs.leaderRuleModal.openModal();
    },

    editRule(item) {
      this.editingRule = item; // Düzenleme modu
      this.$refs.leaderRuleModal.openModal(item);
    },

    async deleteRule(item) {
      const isConfirmed = await confirmDialogDelete({context: this});
      
      if (!isConfirmed) return;

      try {
        const response = await ApiService.delete(`api/leader-board-rules/${item.id}/`);

        if (response && response.status) {
          showSuccess(this.$t('general.delete_rule_success') );
          await this.fetchLeaderBoardData(); // Refresh the table
        } else {
          const errorMessage = response?.data?.message || 'Kural silinirken bir hata oluştu';
          showError(errorMessage);
        }
      } catch (error) {
        showError('API bağlantısında hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        console.error('Delete rule error:', error);
      }
    },

    onRuleAdded() {
      this.fetchLeaderBoardData();
      this.onModalClosed()// Refresh the table when a rule is added
    },

    onRuleUpdated() {
      this.fetchLeaderBoardData(); // Refresh the table when a rule is updated
      this.onModalClosed()
    },

    onModalClosed() {
      this.editingRule = null; // Modal kapandığında editing rule'u temizle
    }
  }
}
</script>

<style scoped src="./LeadersTableManagement.css" />